import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { getMediaUrl } from '../../services/api';
import './LeaderSection.css';

// ─── Avatar Placeholder ───────────────────────────────────────────────────────
const AvatarPlaceholder = ({ name }) => (
  <div className="ls-avatar-placeholder">
    <span>{name ? name.charAt(0).toUpperCase() : '?'}</span>
  </div>
);

// ─── Design 1: Image Left, Content Right ─────────────────────────────────────
const Design1 = ({ leader }) => (
  <div className="ls-card ls-design-1">
    <div className="ls-d1-image">
      {leader.image
        ? <img src={getMediaUrl(leader.image)} alt={leader.name} />
        : <AvatarPlaceholder name={leader.name} />}
    </div>
    <div className="ls-d1-content">
      <div className="ls-badge">Leader</div>
      <h4 className="ls-name">{leader.name}</h4>
      <p className="ls-designation">{leader.designation}</p>
      {leader.description && <p className="ls-desc">{leader.description}</p>}
      <div className="ls-footer">
        {leader.email && (
          <a href={`mailto:${leader.email}`} className="ls-contact-btn"
            onClick={e => e.stopPropagation()} title={leader.email}>✉</a>
        )}
        {leader.phone && (
          <a href={`tel:${leader.phone}`} className="ls-contact-btn"
            onClick={e => e.stopPropagation()} title={leader.phone}>📞</a>
        )}
        <Link to={`/pages/leaders/${leader.id}`} className="ls-view-btn">View →</Link>
      </div>
    </div>
  </div>
);

// ─── Design 2: Image Top, Content Below ──────────────────────────────────────
const Design2 = ({ leader }) => (
  <div className="ls-card ls-design-2">
    <div className="ls-d2-image-wrap">
      {leader.image
        ? <img className="ls-d2-image" src={getMediaUrl(leader.image)} alt={leader.name} />
        : <AvatarPlaceholder name={leader.name} />}
      <div className="ls-d2-gradient" />
      <div className="ls-d2-badge">{leader.designation}</div>
    </div>
    <div className="ls-d2-body">
      <h4 className="ls-name">{leader.name}</h4>
      {leader.description && <p className="ls-desc">{leader.description}</p>}
      <div className="ls-footer">
        {leader.email && (
          <a href={`mailto:${leader.email}`} className="ls-contact-btn"
            onClick={e => e.stopPropagation()} title={leader.email}>✉</a>
        )}
        {leader.phone && (
          <a href={`tel:${leader.phone}`} className="ls-contact-btn"
            onClick={e => e.stopPropagation()} title={leader.phone}>📞</a>
        )}
        <Link to={`/pages/leaders/${leader.id}`} className="ls-view-btn">View →</Link>
      </div>
    </div>
  </div>
);

// ─── Design 3: Circular Image, Centered ──────────────────────────────────────
const Design3 = ({ leader }) => (
  <div className="ls-card ls-design-3">
    <div className="ls-d3-ring">
      <div className="ls-d3-circle">
        {leader.image
          ? <img src={getMediaUrl(leader.image)} alt={leader.name} />
          : <AvatarPlaceholder name={leader.name} />}
      </div>
    </div>
    <h4 className="ls-name">{leader.name}</h4>
    <p className="ls-designation">{leader.designation}</p>
    {leader.description && <p className="ls-desc">{leader.description}</p>}
    <div className="ls-footer ls-footer-center">
      {leader.email && (
        <a href={`mailto:${leader.email}`} className="ls-contact-btn"
          onClick={e => e.stopPropagation()} title={leader.email}>✉</a>
      )}
      {leader.phone && (
        <a href={`tel:${leader.phone}`} className="ls-contact-btn"
          onClick={e => e.stopPropagation()} title={leader.phone}>📞</a>
      )}
      <Link to={`/pages/leaders/${leader.id}`} className="ls-view-btn">Profile →</Link>
    </div>
  </div>
);

// ─── Design 4: Full Image Background with Overlay Text ───────────────────────
const Design4 = ({ leader }) => (
  <div className="ls-card ls-design-4">
    <div
      className="ls-d4-bg"
      style={leader.image ? { backgroundImage: `url(${getMediaUrl(leader.image)})` } : {}}
    >
      {!leader.image && (
        <div className="ls-d4-fallback">
          {leader.name ? leader.name.charAt(0).toUpperCase() : '?'}
        </div>
      )}
    </div>
    <div className="ls-d4-overlay">
      <div className="ls-d4-top">
        <div className="ls-d4-status-dot" />
      </div>
      <div className="ls-d4-bottom">
        <h4 className="ls-name ls-name-white">{leader.name}</h4>
        <p className="ls-designation ls-designation-white">{leader.designation}</p>
        {leader.description && (
          <p className="ls-desc ls-desc-white">{leader.description}</p>
        )}
        <div className="ls-footer">
          {leader.email && (
            <a href={`mailto:${leader.email}`} className="ls-contact-btn ls-contact-btn-white"
              onClick={e => e.stopPropagation()} title={leader.email}>✉</a>
          )}
          {leader.phone && (
            <a href={`tel:${leader.phone}`} className="ls-contact-btn ls-contact-btn-white"
              onClick={e => e.stopPropagation()} title={leader.phone}>📞</a>
          )}
          <Link to={`/pages/leaders/${leader.id}`} className="ls-view-btn ls-view-btn-white">View →</Link>
        </div>
      </div>
    </div>
  </div>
);

// ─── Design 5: Minimal Modern ────────────────────────────────────────────────
const Design5 = ({ leader }) => (
  <div className="ls-card ls-design-5">
    <div className="ls-d5-accent" />
    <div className="ls-d5-inner">
      <div className="ls-d5-thumb">
        {leader.image
          ? <img src={getMediaUrl(leader.image)} alt={leader.name} />
          : <AvatarPlaceholder name={leader.name} />}
      </div>
      <div className="ls-d5-content">
        <h4 className="ls-name">{leader.name}</h4>
        <p className="ls-designation">{leader.designation}</p>
        {leader.description && <p className="ls-desc">{leader.description}</p>}
        <div className="ls-footer">
          {leader.email && (
            <a href={`mailto:${leader.email}`} className="ls-contact-btn"
              onClick={e => e.stopPropagation()} title={leader.email}>✉</a>
          )}
          {leader.phone && (
            <a href={`tel:${leader.phone}`} className="ls-contact-btn"
              onClick={e => e.stopPropagation()} title={leader.phone}>📞</a>
          )}
          <Link to={`/pages/leaders/${leader.id}`} className="ls-view-btn">Profile →</Link>
        </div>
      </div>
    </div>
  </div>
);

const CARD_MAP = {
  design_1: Design1,
  design_2: Design2,
  design_3: Design3,
  design_4: Design4,
  design_5: Design5,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const LeaderSection = () => {
  const [leaders, setLeaders] = useState([]);
  const [config, setConfig] = useState(null);
  const [ready, setReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isInSliderView, setIsInSliderView] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/leader-sections/'),
      api.get('/leaders/'),
    ]).then(([cfgRes, ldrsRes]) => {
      setConfig(cfgRes.data.results?.[0] || null);
      setLeaders(ldrsRes.data.results || []);
      setReady(true);
      // Small delay so CSS transition fires after first paint
      setTimeout(() => setMounted(true), 80);
    }).catch(() => {
      setReady(true);
      setTimeout(() => setMounted(true), 80);
    });
  }, []);

  // Stick-on-slider: hide fixed panel when slider scrolls out of view
  useEffect(() => {
    if (!config) return;
    const { stick_side_layout, stick_only_on_slider } = config;
    if (!stick_side_layout || !stick_only_on_slider) return;

    const onScroll = () => {
      const wrapper = document.querySelector('.slider-with-leader-wrapper');
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      setIsInSliderView(rect.top <= 80 && rect.bottom >= 100);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [config]);

  if (!ready || !config || !config.is_enabled || leaders.length === 0) return null;

  const {
    layout_type, card_design, animation_type,
    background_style, background_opacity, background_color,
    stick_side_layout, stick_only_on_slider,
    title, subtitle,
  } = config;

  // ── Background ──────────────────────────────────────────────────────────────
  const getBg = () => {
    const opacity = background_opacity ?? 80; // ?? not || so 0 works
    if (background_style === 'transparent' || opacity === 0) {
      return {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
      };
    }
    const hex = background_color || '#1a2a4a';
    const alpha = Math.round((opacity / 100) * 255).toString(16).padStart(2, '0');
    return { backgroundColor: `${hex}${alpha}` };
  };

  // ── Animation class from API value ─────────────────────────────────────────
  const animMap = {
    fade_in: 'ls-anim-fade',
    slide_up: 'ls-anim-slide-up',
    zoom_in: 'ls-anim-zoom',
    bounce: 'ls-anim-bounce',
    none: '',
  };
  const animClass = animMap[animation_type] || '';

  // ── Card component ──────────────────────────────────────────────────────────
  const CardComponent = CARD_MAP[card_design] || Design1;

  const isVertical = layout_type === 'left_vertical' || layout_type === 'right_vertical';

  // ── Sticky logic ────────────────────────────────────────────────────────────
  // stick_side_layout=true + stick_only_on_slider=false → fixed full page
  // stick_side_layout=true + stick_only_on_slider=true  → fixed only while slider visible
  const isStickFullPage = isVertical && stick_side_layout && !stick_only_on_slider;
  const isStickOnSlider = isVertical && stick_side_layout && stick_only_on_slider;
  const showAsFixed = isStickFullPage || (isStickOnSlider && isInSliderView);

  // ── Spawn direction (glow animation) ───────────────────────────────────────
  const spawnDir = {
    right_vertical: 'ls-spawn-right',
    left_vertical: 'ls-spawn-left',
    top_horizontal: 'ls-spawn-top',
    bottom_horizontal: 'ls-spawn-bottom',
    overlay_50: 'ls-spawn-bottom',
  }[layout_type] || 'ls-spawn-right';

  // ════════════════════════════════════════════════════════════════════════════
  // OVERLAY_50: All cards in one centered horizontal row straddling slider bottom
  // ════════════════════════════════════════════════════════════════════════════
  if (layout_type === 'overlay_50') {
    return (
      <div
        className={`ls-50-wrapper ls-spawn ls-spawn-bottom ${mounted ? 'ls-spawned' : ''}`}
        style={getBg()}
      >
        {/* {(title || subtitle) && (
          <div className="ls-header ls-header-center">
            {title && <h3 className="ls-title">{title}</h3>}
            {subtitle && <p className="ls-subtitle">{subtitle}</p>}
            <div className="ls-title-line" />
          </div>
        )} */}
        <div className="ls-50-cards-row">
          {leaders.map((leader, i) => (
            <div
              key={leader.id}
              className={`ls-50-card-col ${animClass}`}
              style={{ animationDelay: `${0.55 + i * 0.12}s`, animationFillMode: 'both' }}
            >
              <CardComponent leader={leader} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ALL OTHER LAYOUTS
  // ════════════════════════════════════════════════════════════════════════════

  // Position class (absolute overlay on slider)
  const posClass = {
    right_vertical: 'ls-pos-right',
    left_vertical: 'ls-pos-left',
    top_horizontal: 'ls-pos-top',
    bottom_horizontal: 'ls-pos-bottom',
  }[layout_type] || 'ls-pos-right';

  // Fixed class (overrides absolute when sticky)
  const fixedClass = showAsFixed
    ? `ls-fixed-side ${layout_type === 'left_vertical' ? 'ls-fixed-left' : 'ls-fixed-right'}`
    : '';

  const overlayClass = !showAsFixed ? `ls-overlay-abs ${posClass}` : '';

  return (
    <div
      className={`ls-wrapper ${overlayClass} ${fixedClass} ls-spawn ${spawnDir} ${mounted ? 'ls-spawned' : ''}`}
      style={getBg()}
    >
      <div className="ls-inner">
        {/* {(title || subtitle) && (
          <div className={`ls-header ${!isVertical ? 'ls-header-center' : ''}`}>
            {title && <h3 className="ls-title">{title}</h3>}
            {subtitle && <p className="ls-subtitle">{subtitle}</p>}
            <div className="ls-title-line" />
          </div>
        )} */}

        {/* Vertical layouts: single column */}
        {isVertical && (
          <div className="ls-cards-vertical">
            {leaders.map((leader, i) => (
              <div
                key={leader.id}
                className={`ls-card-col-v ${animClass}`}
                style={{ animationDelay: `${0.55 + i * 0.12}s`, animationFillMode: 'both' }}
              >
                <CardComponent leader={leader} />
              </div>
            ))}
          </div>
        )}

        {/* Horizontal layouts: centered row */}
        {!isVertical && (
          <div className="ls-cards-horizontal">
            {leaders.map((leader, i) => (
              <div
                key={leader.id}
                className={`ls-card-col-h ${animClass}`}
                style={{ animationDelay: `${0.55 + i * 0.12}s`, animationFillMode: 'both' }}
              >
                <CardComponent leader={leader} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderSection;
